import React, { useState, useRef, useEffect } from 'react';
import { Search, MapPin, ChevronDown, X, Check, LayoutGrid, Banknote, SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useProvinces, useDistricts } from '@/hooks/useAddress';
import { useBusinessTypes } from '@/hooks/useListHelper';
import { useAuthStore } from '@/store/authStore';
import { Link } from 'react-router-dom';
import { NumberInput } from '@/components/ui/numberInput';

const INVESTMENT_RANGES = [
  { label: 'Tất cả', min: null, max: null },
  { label: 'Dưới 500 triệu', min: 0, max: 500000000 },
  { label: '500 triệu - 1 tỷ', min: 500000000, max: 1000000000 },
  { label: '1 - 3 tỷ', min: 1000000000, max: 3000000000 },
  { label: '3 - 5 tỷ', min: 3000000000, max: 5000000000 },
  { label: 'Trên 5 tỷ', min: 5000000000, max: null },
];

interface FranchiseFilterProps {
  onSearch: (filters: any) => void;
  initialFilters?: any;
}

export const FranchiseFilter: React.FC<FranchiseFilterProps> = ({ onSearch, initialFilters = {} }) => {
  const { isAuthenticated } = useAuthStore();
  const [search, setSearch] = useState(initialFilters.search || '');

  // Business Type State
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(initialFilters.businessTypeId || null);
  const [tempSelectedTypeId, setTempSelectedTypeId] = useState<string | null>(initialFilters.businessTypeId || null);

  // Location State (manual apply)
  const [tempProvinceId, setTempProvinceId] = useState<number | null>(initialFilters.provinceId || null);
  const [tempDistrictId, setTempDistrictId] = useState<number | null>(initialFilters.districtId || null);
  const [provinceId, setProvinceId] = useState<number | null>(initialFilters.provinceId || null);
  const [districtId, setDistrictId] = useState<number | null>(initialFilters.districtId || null);
  const [wardId, setWardId] = useState<number | null>(initialFilters.wardId || null);

  // Investment range State
  const [minInvestment, setMinInvestment] = useState<number | null>(initialFilters.minInvestment || null);
  const [maxInvestment, setMaxInvestment] = useState<number | null>(initialFilters.maxInvestment || null);

  // Advanced Filters State
  const [minFranchiseFee, setMinFranchiseFee] = useState<number | null>(initialFilters.minFranchiseFee || null);
  const [maxFranchiseFee, setMaxFranchiseFee] = useState<number | null>(initialFilters.maxFranchiseFee || null);
  const [minRoyaltyFee, setMinRoyaltyFee] = useState<number | null>(initialFilters.minRoyaltyFee || null);
  const [maxRoyaltyFee, setMaxRoyaltyFee] = useState<number | null>(initialFilters.maxRoyaltyFee || null);
  const [minAdvertisingFee, setMinAdvertisingFee] = useState<number | null>(initialFilters.minAdvertisingFee || null);
  const [maxAdvertisingFee, setMaxAdvertisingFee] = useState<number | null>(initialFilters.maxAdvertisingFee || null);
  const [startDate, setStartDate] = useState<string | null>(initialFilters.startDate || null);
  const [endDate, setEndDate] = useState<string | null>(initialFilters.endDate || null);

  const [activeDropdown, setActiveDropdown] = useState<'type' | 'location' | 'investment' | 'advanced' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Data Hooks
  const { data: businessTypes = [] } = useBusinessTypes();
  const { data: provinces = [] } = useProvinces(1); // Vietnam
  const { data: districts = [] } = useDistricts(tempProvinceId || undefined);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync temp location states when location dropdown opens
  useEffect(() => {
    if (activeDropdown === 'location') {
      setTempProvinceId(provinceId);
      setTempDistrictId(districtId);
    }
  }, [activeDropdown]);

  // Sync temp type state when type dropdown opens
  useEffect(() => {
    if (activeDropdown === 'type') {
      setTempSelectedTypeId(selectedTypeId);
    }
  }, [activeDropdown]);

  const handleSearch = () => {
    onSearch({
      search: search.trim() || undefined,
      businessTypeId: selectedTypeId || undefined,
      provinceId: provinceId || undefined,
      districtId: districtId || undefined,
      wardId: wardId || undefined,
      minInvestment: minInvestment !== null ? minInvestment : undefined,
      maxInvestment: maxInvestment !== null ? maxInvestment : undefined,
      minFranchiseFee: minFranchiseFee !== null ? minFranchiseFee : undefined,
      maxFranchiseFee: maxFranchiseFee !== null ? maxFranchiseFee : undefined,
      minRoyaltyFee: minRoyaltyFee !== null ? minRoyaltyFee : undefined,
      maxRoyaltyFee: maxRoyaltyFee !== null ? maxRoyaltyFee : undefined,
      minAdvertisingFee: minAdvertisingFee !== null ? minAdvertisingFee : undefined,
      maxAdvertisingFee: maxAdvertisingFee !== null ? maxAdvertisingFee : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    });
    setActiveDropdown(null);
  };

  const applyType = () => {
    setSelectedTypeId(tempSelectedTypeId);
    onSearch({
      ...initialFilters,
      search: search.trim() || undefined,
      businessTypeId: tempSelectedTypeId || undefined,
    });
    setActiveDropdown(null);
  };

  const applyLocation = () => {
    setProvinceId(tempProvinceId);
    setDistrictId(tempDistrictId);
    onSearch({
      ...initialFilters,
      search: search.trim() || undefined,
      businessTypeId: selectedTypeId || undefined,
      provinceId: tempProvinceId || undefined,
      districtId: tempDistrictId || undefined,
    });
    setActiveDropdown(null);
  };

  const resetAll = () => {
    setSearch('');
    setSelectedTypeId(null);
    setTempSelectedTypeId(null);
    setProvinceId(null);
    setDistrictId(null);
    setTempProvinceId(null);
    setTempDistrictId(null);
    setWardId(null);
    setMinInvestment(null);
    setMaxInvestment(null);
    setMinFranchiseFee(null);
    setMaxFranchiseFee(null);
    setMinRoyaltyFee(null);
    setMaxRoyaltyFee(null);
    setMinAdvertisingFee(null);
    setMaxAdvertisingFee(null);
    setStartDate(null);
    setEndDate(null);

    onSearch({
      search: undefined,
      businessTypeId: undefined,
      provinceId: undefined,
      districtId: undefined,
      wardId: undefined,
      minInvestment: undefined,
      maxInvestment: undefined,
      minFranchiseFee: undefined,
      maxFranchiseFee: undefined,
      minRoyaltyFee: undefined,
      maxRoyaltyFee: undefined,
      minAdvertisingFee: undefined,
      maxAdvertisingFee: undefined,
      startDate: undefined,
      endDate: undefined,
    });
    setActiveDropdown(null);
  };

  return (
    <div className="w-full max-w-7xl mx-auto mb-6" ref={containerRef}>
      <div className="relative flex flex-col md:flex-row items-stretch bg-white rounded-2xl shadow-xl border border-gray-100 p-2 gap-2 md:gap-0">

        {/* Business Type Selector */}
        <div className="relative flex-1 group">
          <button
            onClick={() => setActiveDropdown(activeDropdown === 'type' ? null : 'type')}
            className={cn(
              "flex items-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors text-left",
              "hover:bg-gray-50",
              activeDropdown === 'type' && "bg-gray-50"
            )}
          >
            <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center text-orange-500 shrink-0">
              <LayoutGrid size={18} />
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-semibold text-gray-700 truncate">
                {selectedTypeId ? businessTypes.find((t: any) => t.id === selectedTypeId)?.name : "Tất cả loại hình"}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {selectedTypeId && (
                <div
                  onClick={(e) => { e.stopPropagation(); setSelectedTypeId(null); setTempSelectedTypeId(null); handleSearch(); }}
                  className="w-5 h-5 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-400 transition-colors"
                >
                  <X size={12} />
                </div>
              )}
              <ChevronDown size={16} className={cn("text-gray-400 transition-transform", activeDropdown === 'type' && "rotate-180")} />
            </div>
          </button>

          {/* Type Dropdown */}
          {activeDropdown === 'type' && (
            <div className="absolute top-full left-0 mt-3 w-full md:w-[400px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                <h3 className="font-bold text-gray-800">Loại hình kinh doanh</h3>
                <button onClick={() => setTempSelectedTypeId(null)} className="text-xs text-orange-500 font-medium hover:underline">Xóa chọn</button>
              </div>
              <div className="max-h-[300px] overflow-y-auto custom-scrollbar p-2">
                <div className="grid grid-cols-1 gap-1">
                  <button
                    onClick={() => setTempSelectedTypeId(null)}
                    className={cn(
                      "flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-all",
                      !tempSelectedTypeId ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-600 hover:bg-gray-50"
                    )}
                  >
                    <span>Tất cả loại hình</span>
                    {!tempSelectedTypeId && <Check size={16} />}
                  </button>
                  {businessTypes.map((t: any) => (
                    <button
                      key={t.id}
                      onClick={() => setTempSelectedTypeId(t.id)}
                      className={cn(
                        "flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-all",
                        tempSelectedTypeId === t.id ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-600 hover:bg-gray-50"
                      )}
                    >
                      <span>{t.name}</span>
                      {tempSelectedTypeId === t.id && <Check size={16} />}
                    </button>
                  ))}
                </div>
              </div>
              <div className="p-4 bg-gray-50 border-t flex items-center justify-end gap-2">
                <button onClick={() => setActiveDropdown(null)} className="px-6 h-10 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white transition-colors">Hủy</button>
                <button onClick={applyType} className="px-8 h-10 rounded-xl bg-orange-500 text-sm font-bold text-white hover:bg-orange-600 transition-all shadow-md shadow-orange-200">Áp dụng</button>
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

        {/* Search Input */}
        <div className="flex-[1.2] flex items-center px-3 group min-w-0">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500 shrink-0 mr-2">
            <Search size={18} />
          </div>
          <div className="flex-1 relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Tìm theo tên thương hiệu, mô tả..."
              className="w-full text-sm font-semibold text-gray-700 bg-transparent outline-none placeholder:text-gray-300 pr-8"
            />
            {search && (
              <button
                onClick={() => { setSearch(''); onSearch({ ...initialFilters, search: undefined }); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 transition-colors"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

        {/* Location Selector */}
        <div className="relative flex-1 group">
          <button
            onClick={() => setActiveDropdown(activeDropdown === 'location' ? null : 'location')}
            className={cn(
              "flex items-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors text-left",
              "hover:bg-gray-50",
              activeDropdown === 'location' && "bg-gray-50"
            )}
          >
            <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center text-green-500 shrink-0">
              <MapPin size={18} />
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-semibold text-gray-700 truncate">
                {provinceId ? provinces.find((p: any) => p.id === provinceId)?.name : "Toàn quốc"}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {provinceId !== null && (
                <div
                  onClick={(e) => { e.stopPropagation(); setProvinceId(null); setDistrictId(null); setTempProvinceId(null); setTempDistrictId(null); handleSearch(); }}
                  className="w-5 h-5 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-400 transition-colors"
                >
                  <X size={12} />
                </div>
              )}
              <ChevronDown size={16} className={cn("text-gray-400 transition-transform", activeDropdown === 'location' && "rotate-180")} />
            </div>
          </button>

          {/* Location Dropdown */}
          {activeDropdown === 'location' && (
            <div className="absolute top-full right-0 mt-3 w-full md:w-[700px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                <span className="font-bold text-gray-800">Khu vực</span>
                <button onClick={() => { setTempProvinceId(null); setTempDistrictId(null); }} className="text-xs text-orange-500 font-medium hover:underline">Xóa chọn</button>
              </div>

              <div className="flex h-[300px]">
                {/* Left: Provinces */}
                <div className="w-2/5 border-r overflow-y-auto custom-scrollbar">
                  <div className="p-2 space-y-1">
                    {provinces.map((p: any) => (
                      <div
                        key={p.id}
                        onClick={() => { setTempProvinceId(p.id); setTempDistrictId(null); }}
                        className={cn(
                          "flex items-center justify-between px-3 py-2.5 rounded-lg text-sm cursor-pointer transition-all",
                          tempProvinceId === p.id ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-600 hover:bg-gray-50"
                        )}
                      >
                        <span className="truncate">{p.name}</span>
                        <ChevronDown size={14} className="-rotate-90 text-gray-300" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right: Districts */}
                <div className="w-3/5 overflow-y-auto custom-scrollbar p-4">
                  {tempProvinceId ? (
                    <div className="grid grid-cols-1 gap-1">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">QUẬN / HUYỆN</p>
                      {districts.map((d: any) => (
                        <label
                          key={d.id}
                          className={cn(
                            "flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer",
                            tempDistrictId === d.id ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-600 hover:bg-gray-50"
                          )}
                        >
                          <span>{d.name}</span>
                          <div className={cn(
                            "w-4 h-4 rounded border flex items-center justify-center",
                            tempDistrictId === d.id ? "bg-orange-500 border-orange-500" : "border-gray-300"
                          )}>
                            {tempDistrictId === d.id && <Check size={12} className="text-white" />}
                          </div>
                          <input
                            type="radio"
                            name="district"
                            className="hidden"
                            checked={tempDistrictId === d.id}
                            onChange={() => setTempDistrictId(d.id)}
                          />
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-3">
                      <MapPin size={40} className="opacity-20" />
                      <p className="text-sm">Vui lòng chọn Tỉnh/Thành phố</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 bg-gray-50 border-t flex items-center justify-end gap-2">
                <button onClick={() => setActiveDropdown(null)} className="px-6 h-10 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white transition-colors">Hủy</button>
                <button onClick={applyLocation} className="px-8 h-10 rounded-xl bg-orange-500 text-sm font-bold text-white hover:bg-orange-600 transition-all shadow-md shadow-orange-200">Áp dụng</button>
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

        {/* Investment range Selector */}
        <div className="relative flex-1 group">
          <button
            onClick={() => setActiveDropdown(activeDropdown === 'investment' ? null : 'investment')}
            className={cn(
              "flex items-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors text-left",
              "hover:bg-gray-50",
              activeDropdown === 'investment' && "bg-gray-50"
            )}
          >
            <div className="w-8 h-8 rounded-lg bg-yellow-50 flex items-center justify-center text-yellow-600 shrink-0">
              <Banknote size={18} />
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-semibold text-gray-700 truncate">
                {minInvestment === null && maxInvestment === null ? "Vốn đầu tư" :
                  minInvestment !== null && maxInvestment !== null ? `${minInvestment / 1000000} - ${maxInvestment / 1000000} tr` :
                    minInvestment !== null ? `Trên ${minInvestment / 1000000000} tỷ` :
                      maxInvestment !== null ? `Dưới ${maxInvestment / 1000000000} tỷ` : "Vốn đầu tư"}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {(minInvestment !== null || maxInvestment !== null) && (
                <div
                  onClick={(e) => { e.stopPropagation(); setMinInvestment(null); setMaxInvestment(null); handleSearch(); }}
                  className="w-5 h-5 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-400 transition-colors"
                >
                  <X size={12} />
                </div>
              )}
              <ChevronDown size={16} className={cn("text-gray-400 transition-transform", activeDropdown === 'investment' && "rotate-180")} />
            </div>
          </button>

          {/* Investment Dropdown */}
          {activeDropdown === 'investment' && (
            <div className="absolute top-full left-0 mt-3 w-full md:w-[450px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                <h3 className="font-bold text-gray-800">Khoảng vốn đầu tư</h3>
                <button onClick={() => { setMinInvestment(null); setMaxInvestment(null); }} className="text-xs text-orange-500 font-medium hover:underline">Xóa chọn</button>
              </div>

              <div className="max-h-[300px] overflow-y-auto custom-scrollbar p-5">
                <div className="grid grid-cols-2 gap-2 mb-6">
                  {INVESTMENT_RANGES.map((range, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setMinInvestment(range.min);
                        setMaxInvestment(range.max);
                      }}
                      className={cn(
                        "flex items-center gap-2 p-2.5 rounded-xl border text-left transition-all",
                        (minInvestment === range.min && maxInvestment === range.max)
                          ? "border-orange-500 bg-orange-50/50 text-orange-600 font-semibold"
                          : "border-gray-100 hover:border-orange-200 hover:bg-gray-50 text-gray-600"
                      )}
                    >
                      <div className={cn(
                        "w-4 h-4 rounded-full border flex items-center justify-center transition-all",
                        (minInvestment === range.min && maxInvestment === range.max) ? "border-orange-500 border-4" : "border-gray-300"
                      )} />
                      <span className="text-sm">{range.label}</span>
                    </button>
                  ))}
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Hoặc nhập khoảng vốn (VNĐ)</p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 relative">
                      <NumberInput
                        placeholder="Từ"
                        value={minInvestment || ''}
                        onChange={(e) => setMinInvestment(e.target.value ? Number(e.target.value) : null)}
                        className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 focus:shadow-md outline-none text-sm"
                      />
                    </div>
                    <span className="text-gray-400">-</span>
                    <div className="flex-1 relative">
                      <NumberInput
                        placeholder="Đến"
                        value={maxInvestment || ''}
                        onChange={(e) => setMaxInvestment(e.target.value ? Number(e.target.value) : null)}
                        className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 focus:shadow-md outline-none text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-50 border-t flex items-center justify-end gap-2">
                <button onClick={() => setActiveDropdown(null)} className="px-6 h-10 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white transition-colors">Hủy</button>
                <button onClick={handleSearch} className="px-8 h-10 rounded-xl bg-orange-500 text-sm font-bold text-white hover:bg-orange-600 transition-all shadow-md shadow-orange-200">Áp dụng</button>
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

        {/* Advanced Filters Trigger */}
        <div className="relative flex-[0.5] group">
          <button
            onClick={() => setActiveDropdown(activeDropdown === 'advanced' ? null : 'advanced')}
            className={cn(
              "flex items-center justify-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors",
              "hover:bg-gray-50",
              activeDropdown === 'advanced' && "bg-gray-50"
            )}
            title="Lọc nâng cao"
          >
            <SlidersHorizontal size={18} className="text-gray-500" />
            <ChevronDown size={14} className={cn("text-gray-400 transition-transform hidden lg:block", activeDropdown === 'advanced' && "rotate-180")} />
          </button>

          {/* Advanced Dropdown */}
          {activeDropdown === 'advanced' && (
            <div className="absolute top-full right-0 mt-3 w-full md:w-[600px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                <h3 className="font-bold text-gray-800">Lọc nâng cao</h3>
                <button onClick={() => {
                  setMinFranchiseFee(null);
                  setMaxFranchiseFee(null);
                  setMinRoyaltyFee(null);
                  setMaxRoyaltyFee(null);
                  setMinAdvertisingFee(null);
                  setMaxAdvertisingFee(null);
                  setStartDate(null);
                  setEndDate(null);
                }} className="text-xs text-orange-500 font-medium hover:underline">Xóa chọn</button>
              </div>

              {isAuthenticated ? (
                <div className="p-5 grid grid-cols-2 gap-4 max-h-[400px] overflow-y-auto custom-scrollbar">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phí nhượng quyền từ (VNĐ)</label>
                    <NumberInput
                      placeholder="VD: 50,000,000"
                      value={minFranchiseFee || ''}
                      onChange={(e) => setMinFranchiseFee(e.target.value ? Number(e.target.value) : null)}
                      className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 focus:shadow-md outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Đến (VNĐ)</label>
                    <NumberInput
                      placeholder="VD: 500,000,000"
                      value={maxFranchiseFee || ''}
                      onChange={(e) => setMaxFranchiseFee(e.target.value ? Number(e.target.value) : null)}
                      className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 focus:shadow-md outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phí Royalty từ (%)</label>
                    <NumberInput
                      placeholder="VD: 3"
                      value={minRoyaltyFee || ''}
                      onChange={(e) => setMinRoyaltyFee(e.target.value ? Number(e.target.value) : null)}
                      className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 focus:shadow-md outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Đến (%)</label>
                    <NumberInput
                      placeholder="VD: 10"
                      value={maxRoyaltyFee || ''}
                      onChange={(e) => setMaxRoyaltyFee(e.target.value ? Number(e.target.value) : null)}
                      className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 focus:shadow-md outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phí Quảng cáo từ (%)</label>
                    <NumberInput
                      placeholder="VD: 1"
                      value={minAdvertisingFee || ''}
                      onChange={(e) => setMinAdvertisingFee(e.target.value ? Number(e.target.value) : null)}
                      className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 focus:shadow-md outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Đến (%)</label>
                    <NumberInput
                      placeholder="VD: 5"
                      value={maxAdvertisingFee || ''}
                      onChange={(e) => setMaxAdvertisingFee(e.target.value ? Number(e.target.value) : null)}
                      className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 focus:shadow-md outline-none text-sm"
                    />
                  </div>
                  {/* <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Từ ngày</label>
                    <input
                      type="date"
                      value={startDate || ''}
                      onChange={(e) => setStartDate(e.target.value || null)}
                      className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Đến ngày</label>
                    <input
                      type="date"
                      value={endDate || ''}
                      onChange={(e) => setEndDate(e.target.value || null)}
                      className="w-full h-10 px-3 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none text-sm"
                    />
                  </div> */}
                </div>
              ) : (
                <div className="p-5 flex flex-col items-center justify-center text-center py-8">
                  🏪
                  <h4 className="font-bold text-gray-800 mt-2">Đăng nhập để mở khóa lọc nâng cao</h4>
                  <p className="text-xs text-gray-500 mt-1 mb-4 max-w-sm">Mở khóa tính năng lọc chi tiết theo phí Royalty, phí Quảng cáo và lọc mốc thời gian.</p>
                  <Link to="/login" className="px-6 h-9 rounded-xl bg-orange-500 text-white font-bold text-xs flex items-center justify-center hover:bg-orange-600 transition-colors shadow-md shadow-orange-100">Đăng nhập ngay</Link>
                </div>
              )}

              <div className="p-4 bg-gray-50 border-t flex items-center justify-end gap-2">
                <button onClick={() => setActiveDropdown(null)} className="px-6 h-10 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white transition-colors">Hủy</button>
                <button onClick={handleSearch} className="px-8 h-10 rounded-xl bg-orange-500 text-sm font-bold text-white hover:bg-orange-600 transition-all shadow-md shadow-orange-200">Áp dụng</button>
              </div>
            </div>
          )}
        </div>

        {/* Search and Reset Buttons */}
        <div className="p-1 flex gap-2 shrink-0">
          <button
            onClick={resetAll}
            className={cn(
              "hidden lg:flex items-center justify-center gap-1 h-10 md:h-full px-4 rounded-xl border",
              "text-gray-500 text-sm font-medium hover:bg-gray-50 transition-colors"
            )}
            title="Xóa lọc"
          >
            <X size={16} />
          </button>
          <button
            onClick={handleSearch}
            className={cn(
              "flex-1 md:flex-none flex items-center justify-center gap-2 h-10 md:h-full px-8 rounded-xl",
              "bg-orange-500 text-white font-bold text-sm shadow-lg shadow-orange-100",
              "hover:bg-orange-600 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
            )}
          >
            <Search size={18} />
            <span className="md:hidden lg:inline">Tìm kiếm</span>
          </button>
        </div>

      </div>
    </div>
  );
};
